import { Instagram, Phone, Mail, Clock } from "lucide-react";
import { CONTACT, SOCIAL, IMAGES, HOURS } from "@/config/site";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/common/PageHero";
import { Section, SectionHeading } from "@/components/common/Section";
import { Reveal } from "@/components/common/Reveal";
import { LocationCard } from "@/components/common/LocationCard";
import { BookingButton } from "@/components/common/BookingButton";
import { LinkButton } from "@/components/common/LinkButton";
import { ProtoForm } from "@/components/forms/ProtoForm";
import { contactFields } from "@/components/forms/formSchemas";

export default function Contact() {
  useSeo({
    title: "Contact Legacy Spa | Massage Therapy in North Houston",
    description:
      "Contact Legacy Spa in North Houston. Get directions, follow us on Instagram, book an appointment, or request a corporate wellness consultation.",
    path: "/contact",
  });

  return (
    <>
      <PageHero
        eyebrow="Contact"
        titleLines={["Let's connect."]}
        lead="Reach out about in-suite, mobile or corporate wellness — or simply say hello."
        image={IMAGES.massageRoom}
        imageAlt="A calm massage treatment space."
      />

      <Section className="bg-legacy-charcoal">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 space-y-6">
            <SectionHeading eyebrow="Reach Us" title="Here to help." as="h2" />

            <div className="space-y-4">
              <a
                href={CONTACT.phoneHref}
                className="flex items-center gap-3 text-white/70 transition-colors hover:text-legacy-mint"
                data-testid="contact-phone"
              >
                <Phone className="h-5 w-5 text-legacy-mint" aria-hidden="true" />
                {CONTACT.phone}
              </a>
              <a
                href={CONTACT.emailHref}
                className="flex items-center gap-3 text-white/70 transition-colors hover:text-legacy-mint"
                data-testid="contact-email"
              >
                <Mail className="h-5 w-5 text-legacy-mint" aria-hidden="true" />
                {CONTACT.email}
              </a>
              <a
                href={SOCIAL.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/70 transition-colors hover:text-legacy-mint"
                data-testid="contact-instagram"
              >
                <Instagram className="h-5 w-5 text-legacy-mint" aria-hidden="true" />
                {SOCIAL.instagram.handle}
              </a>
            </div>

            <div data-testid="contact-hours">
              <h3 className="flex items-center gap-2 font-heading text-2xl text-white">
                <Clock className="h-5 w-5 text-legacy-mint" aria-hidden="true" />
                Business Hours
              </h3>
              <dl className="mt-4 divide-y divide-white/10 rounded-2xl border border-white/10 bg-white/[0.03] px-5">
                {HOURS.map((h) => (
                  <div key={h.day} className="flex items-center justify-between py-2.5 text-sm">
                    <dt className="text-white/60">{h.day}</dt>
                    <dd className="text-white/85 font-medium">{h.time}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <BookingButton label="Book an Appointment" testId="contact-book-btn" />
              <LinkButton to="/corporate-wellness#consultation" variant="outline" testId="contact-corp-btn">
                Corporate Consultation
              </LinkButton>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ProtoForm
              formId="contact"
              endpoint="contact"
              smsOptIn
              testId="contact-form"
              tone="dark"
              fields={contactFields}
              submitLabel="Send Message"
              consentLabel="I consent to Legacy Spa contacting me about my inquiry. (Please do not include medical history or symptoms.)"
            />
          </div>
        </div>
      </Section>

      <Section className="bg-[#12201d]">
        <SectionHeading eyebrow="Our Suite" title="363 N Sam Houston Pkwy E." as="h2" />
        <div className="mt-12">
          <Reveal>
            <LocationCard />
          </Reveal>
        </div>
      </Section>
    </>
  );
}
